import type { IntervalUnit, Payment } from '@klopotun/contracts';
import {
  formatCivilDate,
  fromMinorUnits,
  parseCivilDate,
  periodAt,
  toMinorUnits,
  type CivilDate,
  type RecurrenceRule,
} from '@klopotun/domain';
import {
  PAST_PAYMENT_STATE,
  type PaymentBodyDraft,
  type PaymentFormValues,
  type PaymentFrequency,
} from '../model/payment-form-values.js';

type Interval = {
  intervalUnit: IntervalUnit;
  intervalCount: number;
};

const PRESET_BY_FREQUENCY: Record<Exclude<PaymentFrequency, 'custom'>, Interval> = {
  monthly: { intervalUnit: 'month', intervalCount: 1 },
  quarterly: { intervalUnit: 'month', intervalCount: 3 },
  half_year: { intervalUnit: 'month', intervalCount: 6 },
  yearly: { intervalUnit: 'year', intervalCount: 1 },
};

const PRESET_FREQUENCIES = ['monthly', 'quarterly', 'half_year', 'yearly'] as const;

const WHOLE_NUMBER_PATTERN = /^\d+$/;

const frequencyOf = (interval: Interval): PaymentFrequency => {
  const matched = PRESET_FREQUENCIES.find((frequency) => {
    const preset = PRESET_BY_FREQUENCY[frequency];
    const isSameUnit = preset.intervalUnit === interval.intervalUnit;

    return isSameUnit && preset.intervalCount === interval.intervalCount;
  });

  return matched ?? 'custom';
};

const toWholeNumber = (value: string): number | null => {
  const trimmed = value.trim();

  if (!WHOLE_NUMBER_PATTERN.test(trimmed)) {
    return null;
  }

  const parsed = Number.parseInt(trimmed, 10);

  return Number.isSafeInteger(parsed) ? parsed : null;
};

/**
 * `toMinorUnits` refuses anything that is not a plain decimal, and a half typed amount is exactly
 * that. The exponent belongs to the currency - "299.00" is 29900 in UAH and 299 in JPY - which is
 * why the arithmetic stays in packages/domain instead of a multiplication by a hundred here.
 */
export const toAmountMinor = (amount: string, currency: string): number | null => {
  try {
    return toMinorUnits(amount.trim().replace(',', '.'), currency);
  } catch {
    return null;
  }
};

const emptyToNull = (value: string): string | null => {
  const trimmed = value.trim();

  return trimmed === '' ? null : trimmed;
};

const toCivilDateOrNull = (value: string): CivilDate | null => {
  try {
    return parseCivilDate(value.trim());
  } catch {
    return null;
  }
};

/**
 * The form asks for one date - the first charge - while the record keeps a start date and an
 * anchor apart. On the way back the first due date of the series is what the person sees, so
 * saving an untouched form changes nothing.
 */
const firstChargeDateOf = (payment: Payment): string => {
  const first = periodAt(
    {
      intervalUnit: payment.intervalUnit,
      intervalCount: payment.intervalCount,
      anchorDay: payment.anchorDay,
      anchorMonth: payment.anchorMonth,
      startDate: parseCivilDate(payment.startDate),
      endDate: payment.endDate === null ? null : parseCivilDate(payment.endDate),
    },
    0,
  );

  return first === null ? payment.startDate : formatCivilDate(first.dueDate);
};

export const intervalOf = (values: PaymentFormValues): Interval | null => {
  if (values.frequency !== 'custom') {
    return PRESET_BY_FREQUENCY[values.frequency];
  }

  const intervalCount = toWholeNumber(values.customIntervalCount);

  return intervalCount === null ? null : { intervalUnit: values.customIntervalUnit, intervalCount };
};

export const intervalUnitOf = (
  frequency: PaymentFrequency,
  customIntervalUnit: IntervalUnit,
): IntervalUnit =>
  frequency === 'custom' ? customIntervalUnit : PRESET_BY_FREQUENCY[frequency].intervalUnit;

/**
 * The schedule step one describes, or null while it is still half typed. What the history step
 * lays its past dates out from - the same rule the server will materialise.
 */
export const recurrenceRuleOfValues = (values: PaymentFormValues): RecurrenceRule | null => {
  const interval = intervalOf(values);
  const chargeDate = toCivilDateOrNull(values.startDate);
  const hasUsableInterval = interval !== null && interval.intervalCount >= 1;

  if (!hasUsableInterval || chargeDate === null) {
    return null;
  }

  return {
    intervalUnit: interval.intervalUnit,
    intervalCount: interval.intervalCount,
    anchorDay: chargeDate.day,
    anchorMonth: interval.intervalUnit === 'year' ? chargeDate.month : null,
    startDate: chargeDate,
    endDate: toCivilDateOrNull(values.endDate),
  };
};

export const emptyPaymentFormValues = (
  today: CivilDate,
  baseCurrency: string,
): PaymentFormValues => ({
  name: '',
  amount: '',
  currency: baseCurrency,
  isVariableAmount: false,
  kind: 'auto_debit',
  frequency: 'monthly',
  customIntervalUnit: 'month',
  customIntervalCount: '1',
  startDate: formatCivilDate(today),
  history: [],
  cardId: '',
  categoryId: '',
  endDate: '',
  cancelUrl: '',
  note: '',
});

export const toPaymentFormValues = (payment: Payment): PaymentFormValues => ({
  name: payment.name,
  amount: payment.amountMinor === null ? '' : fromMinorUnits(payment.amountMinor, payment.currency),
  currency: payment.currency,
  isVariableAmount: payment.amountMode === 'variable',
  kind: payment.kind,
  frequency: frequencyOf({
    intervalUnit: payment.intervalUnit,
    intervalCount: payment.intervalCount,
  }),
  customIntervalUnit: payment.intervalUnit,
  customIntervalCount: String(payment.intervalCount),
  startDate: firstChargeDateOf(payment),
  history: [],
  cardId: payment.cardId ?? '',
  categoryId: payment.categoryId ?? '',
  endDate: payment.endDate ?? '',
  cancelUrl: payment.cancelUrl ?? '',
  note: payment.note ?? '',
});

export const toPaymentBodyDraft = (values: PaymentFormValues): PaymentBodyDraft => {
  const interval = intervalOf(values);
  const intervalUnit = intervalUnitOf(values.frequency, values.customIntervalUnit);
  const isYearly = intervalUnit === 'year';
  const chargeDate = toCivilDateOrNull(values.startDate);

  return {
    name: values.name.trim(),
    kind: values.kind,
    amountMode: values.isVariableAmount ? 'variable' : 'fixed',
    amountMinor: values.isVariableAmount ? null : toAmountMinor(values.amount, values.currency),
    currency: values.currency,
    intervalUnit,
    intervalCount: interval === null ? null : interval.intervalCount,
    anchorDay: chargeDate === null ? null : chargeDate.day,
    anchorMonth: isYearly && chargeDate !== null ? chargeDate.month : null,
    startDate: values.startDate.trim(),
    pastPayments: values.history
      .filter((draft) => draft.state === PAST_PAYMENT_STATE.PAID)
      .map((draft) => ({
        dueDate: draft.dueDate,
        amountMinor: toAmountMinor(draft.amount ?? values.amount, values.currency),
      })),
    skippedDates: values.history
      .filter((draft) => draft.state === PAST_PAYMENT_STATE.SKIPPED)
      .map((draft) => draft.dueDate),
    endDate: emptyToNull(values.endDate),
    cardId: emptyToNull(values.cardId),
    categoryId: emptyToNull(values.categoryId),
    cancelUrl: emptyToNull(values.cancelUrl),
    note: emptyToNull(values.note),
  };
};
